/**
 * `customerAddress` is one free-text field. Reps type it every way there is:
 * "8909 W Vogel Ave, Peoria, AZ 85345", "5494 Valonga St, Las Vegas NV 89148",
 * "2338 W Thomas rd Phoenix AZ 85015", "7535 W Gowan Rd, Las Vegas, NV 89129,
 * USA" (Google). Contracts that ask for separate Street / City / Zip boxes
 * (Jaron, Oct 30, 13433630443) need the parts, so the rep doesn't type twice.
 *
 * `splitAddress` (common/utils) only understands "Street, City, ST ZIP" and
 * must stay as it is: signed M+S / CE contracts display its result (V67, B199).
 *
 * When unsure a part stays in the street rather than being guessed: an
 * autofilled box the rep can read and fix beats a wrong city.
 */
export interface AddressParts {
  city: string
  state: string
  street: string
  zip: string
}

const COUNTRY = /^(usa?|u\.s\.a?\.?|united states( of america)?|ee\.? ?uu\.?)$/i
const ZIP = /^\d{5}(-\d{4})?$/
const UNIT = /^(apt|apartment|unit|ste|suite|bldg|building|lot|#)/i
/** The states HOMENOW sells in: safe to read without a zip next to them. */
const SELLING_STATE = /^(AZ|CA|NV)$/
const STATE_ABBR =
  /^(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)$/i
const STATE_NAME =
  /^(alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|new hampshire|new jersey|new mexico|new york|north carolina|north dakota|ohio|oklahoma|oregon|pennsylvania|rhode island|south carolina|south dakota|tennessee|texas|utah|vermont|virginia|washington|west virginia|wisconsin|wyoming)$/i
const STREET_SUFFIX =
  /^(av|ave|avenue|st|street|dr|drive|rd|road|ln|lane|way|blvd|boulevard|ct|court|cir|circle|pl|place|ter|terrace|pkwy|parkway|hwy|highway|trl|trail|loop)\.?$/i
const CITY_WORD = /^[A-Za-z.'-]+$/
const DIRECTION = /^(n|s|e|w|ne|nw|se|sw|north|south|east|west)\.?$/i

const isState = (text: string): boolean =>
  STATE_ABBR.test(text) || STATE_NAME.test(text)

/** How many trailing tokens are a state, given how sure the context makes us. */
const trailingStateLength = (
  tokens: readonly string[],
  hasZip: boolean,
  hasEarlierPart: boolean,
): number => {
  const last = tokens[tokens.length - 1] ?? ''
  const lastTwo = tokens.slice(-2).join(' ')
  if (hasZip) {
    if (tokens.length >= 2 && STATE_NAME.test(lastTwo)) return 2
    return isState(last) ? 1 : 0
  }
  const whole = tokens.join(' ')
  // ", AZ" or ", Nevada" as a part of its own; "Nevada" as the whole address.
  if ((hasEarlierPart && isState(whole)) || STATE_NAME.test(whole)) {
    return tokens.length
  }
  // "... Phoenix AZ". Never "... Ct" (Court) or "... E Washington" (a street).
  return SELLING_STATE.test(last) ? 1 : 0
}

/** "2338 W Thomas rd Phoenix" -> street + city, only after a known suffix. */
const splitStreetAndCity = (text: string): { city: string; street: string } => {
  const tokens = text.split(' ')
  if (!/^\d/.test(tokens[0] ?? '')) return { city: '', street: text }
  for (let i = tokens.length - 2; i >= 1; i--) {
    if (!STREET_SUFFIX.test(tokens[i]!)) continue
    const city = tokens.slice(i + 1)
    const looksLikeCity =
      city.length <= 4 &&
      city.every((word) => CITY_WORD.test(word) && !UNIT.test(word)) &&
      !DIRECTION.test(city[0]!) &&
      !STREET_SUFFIX.test(city[city.length - 1]!)
    return looksLikeCity
      ? { city: city.join(' '), street: tokens.slice(0, i + 1).join(' ') }
      : { city: '', street: text }
  }
  return { city: '', street: text }
}

export const parseCustomerAddress = (fullAddress: string): AddressParts => {
  const parts = fullAddress
    .split(',')
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  if (parts.length > 1 && COUNTRY.test(parts[parts.length - 1]!)) parts.pop()
  const tail = parts.pop()
  if (!tail) return { city: '', state: '', street: '', zip: '' }

  const hasEarlierPart = parts.length > 0
  const tokens = tail.split(' ')
  // A five-digit first token with nothing before it is a house number.
  let zipAt = -1
  for (let i = tokens.length - 1; i >= (hasEarlierPart ? 0 : 1); i--) {
    if (ZIP.test(tokens[i]!)) {
      zipAt = i
      break
    }
  }
  const zip = zipAt >= 0 ? tokens[zipAt]! : ''
  // "..., NV 89121 unit 156": the unit belongs to the street.
  const afterZip = zipAt >= 0 ? tokens.slice(zipAt + 1).join(' ') : ''
  const head = zipAt >= 0 ? tokens.slice(0, zipAt) : tokens
  const stateLength = trailingStateLength(head, !!zip, hasEarlierPart)
  const state = stateLength ? head.slice(-stateLength).join(' ') : ''
  const rest = (stateLength ? head.slice(0, -stateLength) : head).join(' ')

  let street = ''
  let city = ''
  if (rest && !hasEarlierPart) {
    ;({ city, street } = splitStreetAndCity(rest))
  } else if (rest) {
    if (UNIT.test(rest) || /\d/.test(rest)) {
      street = [...parts, rest].join(', ')
    } else {
      city = rest
      street = parts.join(', ')
    }
  } else if (parts.length >= 2) {
    city = parts.pop()!
    street = parts.join(', ')
  } else if (parts.length === 1) {
    ;({ city, street } = splitStreetAndCity(parts[0]!))
  }
  return {
    city,
    state,
    street: [street, afterZip].filter(Boolean).join(' '),
    zip,
  }
}
